import { useEffect, useState } from "react";
import { Box, Chip, Sheet, Slider, Switch, Typography, Link } from "@mui/joy";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useNavigate } from "react-router";
import { APIGetMyQuizResults, APIGetQuizzes } from "../../api_quiz";

/** Умный фильтр ленты по совместимости (мягкий слой поверх обычных фильтров).
 * props: token, sortByCompat (bool), minCompat (0..100), onChange({sortByCompat, minCompat}) */
export default function SmartMatchFilter({ token, sortByCompat, minCompat, onChange }) {
  const navigate = useNavigate();
  const [hasTests, setHasTests] = useState(null); // null=loading, true/false
  const [available, setAvailable] = useState(false);
  const [localMin, setLocalMin] = useState(minCompat);

  useEffect(() => setLocalMin(minCompat), [minCompat]);

  useEffect(() => {
    let alive = true;
    setAvailable(false);
    setHasTests(null);
    if (!token) return () => { alive = false; };
    (async () => {
      try {
        const [availability, res] = await Promise.all([
          APIGetQuizzes(token, { availability: true }),
          APIGetMyQuizResults(token),
        ]);
        const done = (res?.results || []).some((r) => r.is_completed);
        if (alive) {
          setAvailable(availability?.has_available_quizzes === true);
          setHasTests(done);
        }
      } catch (e) {
        if (alive) setHasTests(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (!available || hasTests === null) return null;

  if (hasTests === false) {
    return (
      <Sheet
        variant="soft"
        color="primary"
        sx={{ borderRadius: "lg", p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}
      >
        <AutoAwesomeRoundedIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography level="body-sm" sx={{ fontWeight: 700 }}>Умный подбор по тестам</Typography>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            Пройдите тесты — и лента начнёт учитывать совместимость.
          </Typography>
        </Box>
        <Link level="body-sm" sx={{ fontWeight: 700 }} onClick={() => navigate("/quizzes")}>
          Пройти
        </Link>
      </Sheet>
    );
  }

  return (
    <Sheet variant="outlined" sx={{ borderRadius: "lg", p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeRoundedIcon color="primary" fontSize="small" />
          <Typography level="body-sm" sx={{ fontWeight: 700 }}>Сортировать по совпадению</Typography>
        </Box>
        <Switch
          checked={sortByCompat}
          onChange={(e) => onChange({ sortByCompat: e.target.checked, minCompat })}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>Мин. совпадение</Typography>
          <Chip size="sm" variant="soft" color={localMin > 0 ? "primary" : "neutral"}>
            {localMin > 0 ? `${localMin}%` : "выкл"}
          </Chip>
        </Box>
        <Slider
          value={localMin}
          min={0}
          max={100}
          step={5}
          valueLabelDisplay="off"
          onChange={(_, v) => setLocalMin(v)}
          onChangeCommitted={(_, v) => onChange({ sortByCompat, minCompat: v })}
        />
      </Box>
    </Sheet>
  );
}